import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import useNotificationHandler from "hooks/useNotificationHandler";
import api from "service/api";
import { QUERY_KEYS } from "library/shared";

export interface VehicleInfoForPrefillDto {
  licensePlate?: string;
  vin?: string;
  registrationCertificateNumber?: string;
  ownerCode?: string;
  // Product of the offer being prefilled ("CASCO"/"MTPL"): for CASCO the backend also
  // returns the vehicle value of the latest previous CASCO offer with the same VIN
  insuranceProduct?: string;
}

export interface PreviousVehicleValue {
  OfferId: string;
  Value: number;
  OfferRequestedAt: string;
}

// Extended response type that includes Client data
export interface VehicleInfoResponse {
  Success: boolean;
  Vehicle: any;
  Client?: {
    Code?: string;
    IsCompany?: boolean;
    Company?: boolean;
    Name?: string;
    FirstName?: string;
    LastName?: string;
    IsExistingInGOS?: boolean;
    IsResident?: boolean;
    Age?: number;
    Experience?: number;
    DriverLicenceDate?: string;
    RegionCode?: string;
    Address?: {
      Email?: string;
      AddressString?: string;
      Phone?: string;
      City?: string;
      Street?: string;
      Index?: string;
    };
  };
  Policy?: {
    EndDate?: string;
    ProductCode?: string;
    Product?: string;
    CoverageTerritory?: string | number;
    ObjectLeased?: boolean | string;
    [key: string]: any;
  };
  // Only for a CASCO prefill, null when no previous CASCO offer has a value
  PreviousCascoVehicleValue?: PreviousVehicleValue | null;
}

const getVehicleInfoForPrefill = async (
  data: VehicleInfoForPrefillDto
): Promise<VehicleInfoResponse> => {
  const response = await api.post(`/vehicles/info`, {
    LicensePlate: data.licensePlate,
    Vin: data.vin,
    RegistrationCertificateNumber: data.registrationCertificateNumber,
    OwnerCode: data.ownerCode,
    InsuranceProduct: data.insuranceProduct,
  });
  return response.data;
};

export function useVehiclePrefillMutation(
  onSuccessCallback?: (data: VehicleInfoResponse) => void,
  onFinally?: () => void,
  // Called when the vehicle register did not find the vehicle (the response can
  // still carry data that does not come from the register, e.g. PreviousCascoVehicleValue)
  onNotFoundCallback?: (data: VehicleInfoResponse) => void
) {
  const { t } = useTranslation();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  return useMutation<VehicleInfoResponse, Error, VehicleInfoForPrefillDto>({
    mutationFn: getVehicleInfoForPrefill,
    onSuccess: (vehicleInfo) => {
      // Check if the prefill was successful
      if (vehicleInfo.Success) {
        // Only show success message and populate form if vehicle was found
        toast.success(
          t(
            "qtg.proposal.prefillSuccess",
            "Vehicle data prefilled successfully"
          )
        );
        if (onSuccessCallback) {
          onSuccessCallback(vehicleInfo);
        }
      } else {
        // Vehicle not found - show info message
        toast.info(
          t(
            "qtg.proposal.prefillNotFound",
            "Vehicle not found with the provided information"
          )
        );
        if (onNotFoundCallback) {
          onNotFoundCallback(vehicleInfo);
        }
      }
    },
    onError: (error: any) => {
      handleAddAxiosErrorNotification(error);
    },
    onSettled: () => {
      // Always clear spinner/loading state regardless of success or failure
      if (onFinally) {
        onFinally();
      }
    },
  });
}
